export type BasicModule = "services" | "products";

import type { TezamPlanCode } from "@/shared/types/onboard.types";

export type TezamTariff = {
    id: string;
    name: string;
    label: string;
    badge?: string;
    featured?: boolean;
    cta: string;
    trialNote?: string;
    onboardPlanCode?: TezamPlanCode;
    priceAmount: number;
    priceCurrency: "BYN" | "USD";
    pricePeriod?: string;
    yearlyAmount?: number;
    bullets: string[];
    excluded?: string[];
};

/** Утверждённая сетка Tezam (BYN / мес). Онбординг всегда стартует с demo. */
export const PRICING = {
    start: 50,
    business: 99,
    pro: 189,
    holding: 349,
    addonSeo: 29,
    addonAi: 35,
    addonAccessPoint: 40,
    addonEmbed: 19,
} as const;

export const YEARLY = {
    start: 450,
    business: 900,
    pro: 1680,
    holding: 2988,
} as const;

export const TRIAL_NOTE = "14 дней бесплатно";

/** Разовые услуги наполнения и рекламы (не подписка). */
export const SETUP_SERVICES = {
    title: "Наполнение под ключ",
    badge: "Разовые услуги",
    description:
        "Мы сами заполним каталог Tezam и при необходимости настроим рекламные кабинеты — чтобы вы запускались с готовой витриной.",
    items: [
        {
            label: "Наполнение сервиса Tezam",
            detail: "до 500 товаров / услуг",
            amount: 650,
        },
        {
            label: "Наполнение сервиса Tezam",
            detail: "до 3 000 товаров / услуг",
            amount: 1000,
        },
        {
            label: "Настройка рекламного кабинета Яндекс",
            detail: "разово",
            amount: 450,
        },
        {
            label: "Настройка рекламного кабинета Google",
            detail: "разово",
            amount: 450,
        },
    ],
    cta: "Заказать наполнение",
} as const;

export const basicTariff: TezamTariff = {
    id: "start",
    name: "Стартер",
    label: "Для самозанятых (НПД), ремесленников и КФХ",
    badge: "НПД / ремесло / КФХ",
    cta: "Попробовать бесплатно",
    trialNote: TRIAL_NOTE,
    onboardPlanCode: "start",
    priceAmount: PRICING.start,
    priceCurrency: "BYN",
    pricePeriod: "мес",
    yearlyAmount: YEARLY.start,
    bullets: [
        "Telegram Mini App + веб-витрина + CRM в одном окне",
        "Запись на услуги, календарь, база клиентов",
        "Новости / рассылки, бонусы и промокоды",
        "До 20 товаров, 20 услуг, 3 сотрудника, 1 точка",
    ],
    excluded: ["Склад FIFO", "P&L / зарплаты", "СКУД"],
};

export const fullTariff: TezamTariff = {
    id: "business",
    name: "Бизнес",
    label: "Для салонов, барбершопов, розницы, услуг и островков в ТЦ",
    badge: "Хит продаж",
    featured: true,
    cta: "Попробовать бесплатно",
    trialNote: TRIAL_NOTE,
    onboardPlanCode: "business",
    priceAmount: PRICING.business,
    priceCurrency: "BYN",
    pricePeriod: "мес",
    yearlyAmount: YEARLY.business,
    bullets: [
        "Всё из «Стартер» + склад FIFO и техкарты",
        "Кассы, P&L и расчёт зарплат",
        "Колесо фортуны, рефералка, сертификаты, рассылки",
        "До 500 товаров и 500 услуг",
    ],
};

export const proTariff: TezamTariff = {
    id: "pro",
    name: "Про / СКУД",
    label: "Для фитнес-клубов, залов, коворкингов, студий и кафе/ресторанов (1 объект)",
    badge: "Фитнес / HoReCa",
    cta: "Попробовать бесплатно",
    trialNote: TRIAL_NOTE,
    onboardPlanCode: "pro",
    priceAmount: PRICING.pro,
    priceCurrency: "BYN",
    pricePeriod: "мес",
    yearlyAmount: YEARLY.pro,
    bullets: [
        "Всё из «Бизнес» + Access Control (QR/NFC)",
        "Booking 2.0: ресурсы и групповые занятия",
        "HoReCa: залы, столы, KDS, принтеры",
        "Инвойсы и акты",
    ],
};

export const holdingTariff: TezamTariff = {
    id: "holding",
    name: "Холдинг",
    label: "Для сетей фитнес-клубов, ресторанных групп и мульти-бизнесов (3–10 точек)",
    badge: "Сети",
    cta: "Обсудить сеть",
    trialNote: TRIAL_NOTE,
    onboardPlanCode: "holding",
    priceAmount: PRICING.holding,
    priceCurrency: "BYN",
    pricePeriod: "мес",
    yearlyAmount: YEARLY.holding,
    bullets: [
        "Всё из «Про» на объекты холдинга",
        "Фикс 349 BYN/мес (или 249 при оплате за год)",
        "Единый договор без доплаты за точку",
        "От 3 до 10 точек в одном договоре",
    ],
};

export const getBasicTariffName = (module: BasicModule, withAddon: boolean) => {
    const moduleLabel = module === "services" ? "Услуги" : "Товары";
    if (!withAddon) return `Стартер (${moduleLabel})`;
    const addonLabel = module === "services" ? "Товары" : "Услуги";
    return `Стартер (${moduleLabel} + ${addonLabel})`;
};

export const getBasicPrice = (_withAddon: boolean) => PRICING.start;

export const formatTariffPrice = (amount: number, currency: "BYN" | "USD", period?: string) => {
    const formatted = new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
    }).format(amount);

    return period ? `${formatted} / ${period}` : formatted;
};

export const calculateConfiguratorPrice = (_config: {
    secondModule: boolean;
    bonus?: boolean;
    bonusSystem?: boolean;
    extendedLimits?: boolean;
    expandedLimits?: boolean;
}) => PRICING.business;
